import { Box, Flex, Table, Text } from '@chakra-ui/react'
import { Users } from 'lucide-react'
import React from 'react'

import { useReportingI18n } from '../../../i18n'
import type {
  QueueAgent,
  QueueDashboardAgentActions,
  QueueDashboardCustomStatus,
  QueueDashboardExtension,
} from '../types'
import AgentRow from './AgentRow'

export interface InactiveAgentsTableProps {
  agents: QueueAgent[]
  isLoading: boolean
  customStatuses?: QueueDashboardCustomStatus[]
  extensions?: QueueDashboardExtension[]
  showActions?: boolean
  agentActions?: QueueDashboardAgentActions
}

const Thead = Table.Header
const Tbody = Table.Body
const Tr = Table.Row
const Th = Table.ColumnHeader

const tableHeader = (title: string, shrink?: boolean) => {
  return (
    <Th
      padding="12px 16px"
      bg="gray.subtle"
      color="fg.default"
      fontSize="0.75rem"
      fontWeight="600"
      textTransform="none"
      width={shrink ? '1%' : undefined}
      whiteSpace={shrink ? 'nowrap' : undefined}
    >
      {title}
    </Th>
  )
}

const EmptyState: React.FC = () => {
  const { text } = useReportingI18n()

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      padding="48px"
      color="fg.muted"
      data-testid="inactive-agents-table-empty-state"
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        width="80px"
        height="80px"
        borderRadius="full"
        bg="bg.muted"
        mb="16px"
      >
        <Box as={Users} width="36px" height="36px" color="fg.muted" />
      </Flex>
      <Text fontSize="1rem" fontWeight="600" color="fg.default">
        {text(
          'queueDashboard.inactiveAgentsTable.emptyState.title',
          'No Agents found',
        )}
      </Text>
      <Text fontSize="0.875rem" mt="4px" color="fg.muted">
        {text(
          'queueDashboard.inactiveAgentsTable.emptyState.description',
          'There is currently no information showing for the current Queue',
        )}
      </Text>
    </Flex>
  )
}

const InactiveAgentsTable: React.FC<InactiveAgentsTableProps> = ({
  agents,
  isLoading,
  customStatuses = [],
  extensions = [],
  showActions = false,
  agentActions,
}) => {
  const { text } = useReportingI18n()

  if (!isLoading && agents.length === 0) {
    return <EmptyState />
  }

  return (
    <Table.Root variant="line" size="sm">
      <Thead>
        <Tr>
          {tableHeader(
            text('queueDashboard.inactiveAgentsTable.agent', 'Agent'),
          )}
          {tableHeader(text('queueDashboard.inactiveAgentsTable.ext', 'Ext'))}
          {tableHeader(
            text(
              'queueDashboard.inactiveAgentsTable.lastLoggedOut',
              'Last Logged out',
            ),
          )}
          {tableHeader(
            text('queueDashboard.inactiveAgentsTable.status', 'Status'),
          )}
          {showActions &&
            tableHeader(
              text('queueDashboard.inactiveAgentsTable.action', 'Action'),
              true,
            )}
        </Tr>
      </Thead>
      <Tbody>
        {agents.map((agent, index) => (
          <AgentRow
            key={agent.extension}
            agent={agent}
            isActive={false}
            index={index}
            customStatuses={customStatuses}
            extensions={extensions}
            showActions={showActions}
            agentActions={agentActions}
          />
        ))}
      </Tbody>
    </Table.Root>
  )
}

export default InactiveAgentsTable
